import Select from '../../../components/Select/Select';
import Datepicker from '../../../components/Inputs/Datepicker/Datepicker';
import SelectComplectations from '../../../components/Selects/SelectComplectations';
import SelectCarTransmissions from '../../../components/Selects/SelectCarTransmissions';
import SelectWd from '../../../components/Selects/SelectWd';
import SelectColor from '../../../components/Selects/SelectColor';
import SelectCity from '../../../components/Selects/SelectCity';
import SelectDc from '../../../components/Selects/SelectDc';
import SelectMarks from '../../../components/Selects/SelectMarks';
import SelectModels from '../../../components/Selects/SelectModels';
import { PURCHASE_PROCESS_TYPES } from '../../../constants/carOrigin';

export const ENGINE_TYPE_OPTIONS = [
  { value: 'PETROL', label: 'Бензин' },
  { value: 'DIESEL', label: 'Дизель' },
  { value: 'GAS_PETROL', label: 'Газ-бензин' },
  { value: 'GAS', label: 'Газ' },
  { value: 'HYBRID', label: 'Гибрид' },
  { value: 'ELECTRIC', label: 'Электричество' },
];

/**
 * Поля характеристик автомобиля — без поля «Происхождение», его каждый экран
 * добавляет сам (или не добавляет, если тип зафиксирован).
 *
 * @param {Object} carData — текущие значения для defaultValue
 * @param {Function} onMarkChange — вызывается при смене марки
 * @param {Function} onModelChange — (modelOption, markOption) при смене модели
 * @param {Function} getContainerId — контейнер для подгрузки комплектаций
 * @param {Function} onDcChange — вызывается при смене дилерского центра
 * @param {boolean} singleDc — один ДЦ вместо мультиселекта
 * @param {boolean} usedDcOnly — показывать только АСП (точки продаж БУ-авто)
 * @param {boolean} withTechPassport — добавить номер и дату техпаспорта (договор поручения)
 * @param {boolean} mileageRequired — пробег обязателен всегда (иначе — только для б/у по origin)
 * @param {boolean} usedDictionaries — б/у-процесс: в марках/моделях/комплектациях
 *   показываем и заведённые под б/у, и разрешаем завести новые прямо в селекте.
 *   Ограничение по маркам ДЦ менеджера при этом снимаем: по договору поручения
 *   принимают машину любой марки, а не только той, которой торгует ДЦ.
 * @param {boolean} creatableDictionaries — разрешить заводить марки, модели и
 *   комплектации, не переходя в б/у-процесс. Нужно серым дилерам (JetQ, MEGA):
 *   они торгуют любыми марками, и ждать, пока справочник пополнит администратор,
 *   значит не завести машину. Заведённое попадает в обычный справочник.
 */
export const buildCarInfoInputs = ({
  carData = {},
  onMarkChange,
  onModelChange,
  onDcChange,
  getContainerId,
  singleDc = false,
  usedDcOnly = false,
  withTechPassport = false,
  mileageRequired = false,
  usedDictionaries = false,
  creatableDictionaries = false,
}) => [
  {
    name: 'mark',
    component: SelectMarks,
    // Серому дилеру ограничение по маркам его ДЦ тоже ни к чему: он торгует любыми.
    byUser: !usedDictionaries && !creatableDictionaries,
    used: usedDictionaries,
    creatable: usedDictionaries || creatableDictionaries,
    label: 'Марка',
    placeholder: 'Выберите из списка',
    rules: { required: true },
    defaultValue: carData?.mark,
    onChangeCustom: () => onMarkChange && onMarkChange(),
  },
  {
    name: 'model',
    component: SelectModels,
    used: usedDictionaries,
    creatable: usedDictionaries || creatableDictionaries,
    dependency: 'mark',
    dependencyAccessor: (obj) => obj?.value,
    label: 'Модель',
    placeholder: 'Выберите из списка',
    rules: { required: true },
    defaultValue: carData?.model,
    onChangeCustom: (modelOption, markOption) => onModelChange && onModelChange(modelOption, markOption),
  },
  {
    type: 'selectComplectations',
    name: 'complectationId',
    component: SelectComplectations,
    used: usedDictionaries,
    creatable: usedDictionaries || creatableDictionaries,
    dependency: ['mark', 'model'],
    dependencyAccessor: (arr) => arr?.map((o) => o?.value).join('|'),
    params: () => (getContainerId ? getContainerId() : null),
    defaultValue: carData?.complectationId,
    rules: { required: true },
  },
  {
    type: 'input',
    name: 'issueYear',
    value: undefined,
    label: 'Год выпуска',
    placeholder: 'Укажите год выпуска',
    inputType: 'number',
    rules: { required: true },
    defaultValue: carData?.issueYear,
  },
  {
    type: 'input',
    name: 'vin',
    value: undefined,
    label: 'VIN',
    placeholder: 'Укажите VIN',
    maxLength: 17,
    rules: { required: true },
    defaultValue: carData?.vin,
  },
  ...(withTechPassport
    ? [
        {
          // texNumber — то же поле «Техпаспорт», что банки уже требуют в сделке,
          // поэтому пишем в него, а не заводим второе.
          type: 'input',
          name: 'texNumber',
          label: 'Номер техпаспорта',
          placeholder: 'Укажите номер',
          rules: { required: true },
          defaultValue: carData?.texNumber,
        },
        {
          name: 'texDate',
          component: Datepicker,
          label: 'Дата выдачи техпаспорта',
          placeholder: 'дд-мм-гггг',
          rules: { required: true },
          defaultValue: carData?.texDate,
        },
      ]
    : []),
  {
    type: 'selectColor',
    name: 'color',
    component: SelectColor,
    value: undefined,
    label: 'Цвет кузова',
    placeholder: 'Укажите цвет',
    defaultValue: carData?.color,
    rules: { required: true },
  },
  {
    type: 'input',
    name: 'price',
    value: undefined,
    label: 'Цена',
    placeholder: 'Укажите цену',
    format: 'number',
    rules: { required: true },
    defaultValue: carData?.price,
  },
  {
    type: 'input',
    name: 'engine_volume',
    value: undefined,
    label: 'Объем двигателя',
    placeholder: 'Укажите объем двигателя',
    defaultValue: carData?.engine_volume,
  },
  {
    type: 'input',
    name: 'power',
    value: undefined,
    label: 'Мощность',
    placeholder: 'Укажите мощность',
    defaultValue: carData?.power,
  },
  {
    type: 'selectTransmissions',
    name: 'transmissionId',
    component: SelectCarTransmissions,
    value: undefined,
    label: 'Тип коробки передач',
    placeholder: 'Укажите тип',
    defaultValue: carData?.transmissionId,
    rules: { required: true },
  },
  {
    type: 'selectWd',
    name: 'wdId',
    component: SelectWd,
    value: undefined,
    label: 'Тип привода',
    placeholder: 'Укажите тип',
    defaultValue: carData?.wdId,
    rules: { required: true },
  },
  {
    type: 'selectCity',
    name: 'cityId',
    component: SelectCity,
    value: undefined,
    label: 'Город',
    defaultValue: carData?.cityId,
    rules: { required: true },
  },
  {
    name: 'dc',
    component: SelectDc,
    label: 'Дилерский центр',
    placeholder: 'Выберите из списка',
    defaultValue: carData?.dc,
    isMulti: !singleDc,
    used: usedDcOnly || undefined,
    onChangeCustom: (value) => onDcChange && onDcChange(value),
  },
  {
    type: 'input',
    name: 'mileage',
    value: undefined,
    label: 'Пробег, км',
    placeholder: 'Укажите пробег',
    inputType: 'number',
    defaultValue: carData?.mileage,
    rules: mileageRequired
      ? { required: true }
      : {
          validate: (value, getValues) => {
            const origin = getValues('origin');
            const originValue = origin?.value;
            const isUsed =
              typeof originValue === 'string' && originValue.startsWith('factory_')
                ? origin?.isUsedFactory
                : PURCHASE_PROCESS_TYPES.includes(originValue);
            if (isUsed && (!value || value.toString().trim() === '')) {
              return 'Пробег обязателен для б/у автомобилей';
            }
            return true;
          },
        },
  },
  {
    type: 'select',
    name: 'engineType',
    component: Select,
    value: undefined,
    label: 'Тип двигателя',
    placeholder: 'Выберите из списка',
    options: ENGINE_TYPE_OPTIONS,
    defaultValue: carData?.engineType,
    rules: { required: true },
  },
];

export default buildCarInfoInputs;
